import React, { useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  deleteUser,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, query, where, getDocs, collection, runTransaction } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import InstallButton from '../components/InstallButton';
import { useAuth } from './AuthContext';


const checkUsernameExists = async (username) => {
  const q = query(collection(db, 'users'), where('username', '==', username));
  const snap = await getDocs(q);
  return !snap.empty;
};

const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
const validatePassword = (pwd) => typeof pwd === 'string' && pwd.length >= 6;

function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '', username: '', general: '' });
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, authLoading } = useAuth();
  const redirectTo =
    typeof location.state?.redirectTo === 'string'
      ? location.state.redirectTo
      : '/profile';
  const authMessage =
    typeof location.state?.authMessage === 'string'
      ? location.state.authMessage
      : '';


  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.ctrlKey) return;
      if (event.key === '1') setActiveTab('login');
      else if (event.key === '2') setActiveTab('register');
      else if (event.key === 'h') navigate('/');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && (currentUser || auth.currentUser)) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, currentUser, navigate, redirectTo]);

  const clearErrors = () => setErrors({ email: '', password: '', username: '', general: '' });

  const switchTab = (tab) => {
    setActiveTab(tab);
    clearErrors();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    clearErrors();
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/user-not-found') setErrors((state) => ({ ...state, email: 'Utente non trovato' }));
      else if (code === 'auth/wrong-password') setErrors((state) => ({ ...state, password: 'Password errata' }));
      else if (code === 'auth/too-many-requests') {
        setErrors((state) => ({ ...state, general: 'Accesso bloccato temporaneamente. Riprova più tardi.' }));
      } else {
        setErrors((state) => ({ ...state, general: 'Email o password errati' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    clearErrors();
    setIsLoading(true);

    const trimmedUsername = username.trim();
    const newErrors = { email: '', password: '', username: '', general: '' };

    if (!trimmedUsername) newErrors.username = 'Inserisci un username valido';
    if (!validateEmail(email)) newErrors.email = 'Email non valida';
    if (!validatePassword(password)) newErrors.password = 'La password deve contenere almeno 6 caratteri';

    if (newErrors.email || newErrors.password || newErrors.username) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    if (await checkUsernameExists(trimmedUsername)) {
      setErrors((state) => ({ ...state, username: 'Username già in uso' }));
      setIsLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      try {
        await runTransaction(db, async (transaction) => {
          const usernameRef = doc(db, 'usernames', trimmedUsername.toLowerCase());
          const usernameSnap = await transaction.get(usernameRef);
          if (usernameSnap.exists()) throw new Error('USERNAME_TAKEN');

          transaction.set(usernameRef, { uid: user.uid, createdAt: new Date() });

          const userRef = doc(db, 'users', user.uid);
          transaction.set(userRef, {
            email: email.trim(),
            username: trimmedUsername,
            factionId: null,
            favoriteWrestler: [],
          });
        });

        navigate(redirectTo, { replace: true });
      } catch (txErr) {
        try {
          await deleteUser(user);
        } catch (deleteErr) {
          // ignore
        }

        if (txErr?.message === 'USERNAME_TAKEN' || txErr?.code === 'USERNAME_TAKEN') {
          setErrors((state) => ({ ...state, username: 'Username già in uso' }));
        } else {
          setErrors((state) => ({ ...state, general: 'Errore durante la registrazione' }));
        }
      }
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/email-already-in-use') setErrors((state) => ({ ...state, email: 'Email già in uso' }));
      else if (code === 'auth/weak-password') setErrors((state) => ({ ...state, password: 'Password troppo debole' }));
      else if (code === 'auth/invalid-email') setErrors((state) => ({ ...state, email: 'Email non valida' }));
      else setErrors((state) => ({ ...state, general: 'Errore durante la registrazione' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    clearErrors();
    if (!email) {
      setErrors((state) => ({ ...state, email: "Inserisci l'email per il recupero" }));
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setErrors((state) => ({ ...state, general: 'Email di recupero inviata. Controlla la tua casella.' }));
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/user-not-found') setErrors((state) => ({ ...state, email: 'Utente non trovato' }));
      else setErrors((state) => ({ ...state, general: "Errore durante l'invio della mail di recupero" }));
    }
  };

  if (!authLoading && (currentUser || auth.currentUser)) {
    return null;
  }

  return (
    <div className='auth-hero-panel'>
      <section className="">
        <div className="auth-hero-panel__content">

          <div className="auth-quick-links d-flex gap-3">
            <Link to="/rules" className="auth-quick-link flex-grow-1">
              <i className="bi bi-journal-text" />
              <span>
                <strong>Regolamento</strong>
                <small>Controlla regole, punteggi e modalità di gioco.</small>
              </span>
            </Link>
            <Link to="/wrestlers" className="auth-quick-link flex-grow-1">
              <i className="bi bi-people" />
              <span>
                <strong>Wrestlers</strong>
                <small>Esplora l'elenco di wrestler disponibili.</small>
              </span>
            </Link>
          </div>

          <div className="auth-install-card">
            <div>
              <strong>Installa l&apos;app</strong>
              <p className="mb-0">Aggiungi FantaWrestling alla home per aprirlo come app.</p>
            </div>
            <InstallButton />
          </div>
        </div>
      </section>

      <section className="auth-form-panel">
        <div className="auth-form-panel__top">
          <div>
            <h2 className="auth-panel-title">{activeTab === 'login' ? 'Bentornato' : 'Crea il tuo account'}</h2>
            <p className="auth-panel-copy">
              {activeTab === 'login'
                ? 'Accedi per tornare subito ai tuoi profilo e alle tue fazioni.'
                : 'Registrati per iniziare a competere e gestire il tuo profilo.'}
            </p>
          </div>
        </div>

        {authMessage && (
          <div className="auth-error auth-error--success mb-3">
            <i className="bi bi-shield-lock" />
            {authMessage}
          </div>
        )}

        <div className="auth-switch" role="tablist" aria-label="Tipo autenticazione">
          <button
            className={`auth-switch-btn ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => switchTab('login')}
            type="button"
          >
            <i className="bi bi-box-arrow-in-right me-2" />
            <span>Login</span>
          </button>
          <button
            className={`auth-switch-btn ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => switchTab('register')}
            type="button"
          >
            <i className="bi bi-person-plus me-2" />
            <span>Registrati</span>
          </button>
        </div>

        <div className="auth-content">
          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <div className="input-group-auth">
                <label htmlFor="login-email">Email</label>
                <div className="input-with-icon">
                  <i className="bi bi-envelope" />
                  <input
                    id="login-email"
                    type="email"
                    placeholder="tua@email.com"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (errors.email) setErrors((state) => ({ ...state, email: '' }));
                    }}
                    required
                  />
                </div>
                {errors.email && <div className="auth-error">{errors.email}</div>}
              </div>

              <div className="input-group-auth">
                <label htmlFor="login-password">Password</label>
                <div className="input-with-icon">
                  <i className="bi bi-lock" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="La tua password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      if (errors.password) setErrors((state) => ({ ...state, password: '' }));
                    }}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
                {errors.password && <div className="auth-error">{errors.password}</div>}
              </div>

              <div className="auth-actions">
                <button type="button" className="auth-link text-brand-primary-ultralight" onClick={handlePasswordReset}>
                  Password dimenticata?
                </button>
              </div>

              {errors.general && (
                <div className={`auth-error ${errors.general.includes('inviata') ? 'auth-error--success' : ''}`}>
                  <i className={`bi ${errors.general.includes('inviata') ? 'bi-check-circle' : 'bi-exclamation-circle'}`} />
                  {errors.general}
                </div>
              )}

              <button
                type="submit"
                className="auth-submit"
                disabled={isLoading || !email || !password || errors.email || errors.password}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right me-2" />
                    Accedi
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="input-group-auth">
                <label htmlFor="register-username">Username</label>
                <div className="input-with-icon">
                  <i className="bi bi-person" />
                  <input
                    id="register-username"
                    type="text"
                    placeholder="Scegli un username"
                    value={username}
                    onChange={(event) => {
                      setUsername(event.target.value);
                      if (errors.username) setErrors((state) => ({ ...state, username: '' }));
                    }}
                    required
                  />
                </div>
                {errors.username && <div className="auth-error">{errors.username}</div>}
              </div>

              <div className="input-group-auth">
                <label htmlFor="register-email">Email</label>
                <div className="input-with-icon">
                  <i className="bi bi-envelope" />
                  <input
                    id="register-email"
                    type="email"
                    placeholder="tua@email.com"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (errors.email) setErrors((state) => ({ ...state, email: '' }));
                    }}
                    required
                  />
                </div>
                {errors.email && <div className="auth-error">{errors.email}</div>}
              </div>

              <div className="input-group-auth">
                <label htmlFor="register-password">Password</label>
                <div className="input-with-icon">
                  <i className="bi bi-lock" />
                  <input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Crea una password sicura"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      if (errors.password) setErrors((state) => ({ ...state, password: '' }));
                    }}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
                <div className="password-hint">
                  <i className="bi bi-info-circle" />
                  Minimo 6 caratteri
                </div>
                {errors.password && <div className="auth-error">{errors.password}</div>}
              </div>

              {errors.general && <div className="auth-error"><i className="bi bi-exclamation-circle" />{errors.general}</div>}

              <button
                type="submit"
                className="auth-submit"
                disabled={isLoading || !email || !password || !username || errors.email || errors.password || errors.username}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2" />
                    Registrati
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

export default Auth;
